import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {SalesPersonModel} from '../../../../DataModels/salesperson-model';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';

@Component({
  selector: 'app-salesperson-form',
  templateUrl: './salesperson-form.component.html',
  styleUrls: ['./salesperson-form.component.css'],
  standalone: false
})
export class SalespersonFormComponent implements OnInit {
  salespersonID: string;
  salesperson: SalesPersonModel;
  salespersonForm: UntypedFormGroup;

  submitted = false;
  originalSnapshot: any = {};

  sysUsers: any[] = [];
  entities: any[] = [];
  itemTypes: any[] = [];
  itemCategories: any[] = [];
  itemGroups: any[] = [];
  items: any[] = [];

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title,
    private nav: NavigationService
  ) {
    this.createForm();
    this.salespersonID = this.route.snapshot.params.id;
  }

  get f() {
    return this.salespersonForm.controls;
  }

  private createForm() {
    this.salespersonForm = this.formBuilder.group({
      name: new UntypedFormControl(''),
      rate: new UntypedFormControl(''),
      active: new UntypedFormControl(true),
      contractor: new UntypedFormControl(false),
      default_bill_rate: new UntypedFormControl(''),
      default_cost_rate: new UntypedFormControl(''),
      type_id: new UntypedFormControl(''),
      category_id: new UntypedFormControl(''),
      group_id: new UntypedFormControl(''),
      item_id: new UntypedFormControl(''),
      user_id: new UntypedFormControl(''),
      vendor_id: new UntypedFormControl(''),
    });
  }

  // ----- Numeric formatting helpers -----
  stripBillRateSeparators(): void {
    this.stripCtrl('default_bill_rate');
  }

  formatBillRate(): void {
    this.formatCtrl('default_bill_rate');
  }

  stripCostRateSeparators(): void {
    this.stripCtrl('default_cost_rate');
  }

  formatCostRate(): void {
    this.formatCtrl('default_cost_rate');
  }

  private stripCtrl(name: string): void {
    const ctrl = this.salespersonForm.get(name);
    if (!ctrl) {
      return;
    }
    const v = ctrl.value;
    if (v === null || v === undefined) {
      return;
    }
    ctrl.setValue(String(v).replace(/,/g, ''), {emitEvent: false});
  }

  private formatCtrl(name: string): void {
    const ctrl = this.salespersonForm.get(name);
    if (!ctrl) {
      return;
    }
    const formatted = this.formatNumberToTwo(ctrl.value);
    ctrl.setValue(formatted !== null ? formatted : '', {emitEvent: false});
  }

  // Strip thousand separators while editing
  stripRateSeparators(): void {
    const ctrl = this.salespersonForm.get('rate');
    if (!ctrl) {
      return;
    }
    const v = ctrl.value;
    if (v === null || v === undefined) {
      return;
    }
    const raw = String(v).replace(/,/g, '');
    ctrl.setValue(raw, {emitEvent: false});
  }

  // Format with thousands separators and 2 decimals on blur
  formatRate(): void {
    const ctrl = this.salespersonForm.get('rate');
    if (!ctrl) {
      return;
    }
    const formatted = this.formatNumberToTwo(ctrl.value);
    if (formatted !== null) {
      ctrl.setValue(formatted, {emitEvent: false});
    } else {
      ctrl.setValue('', {emitEvent: false});
    }
  }

  private formatNumberToTwo(val: any): string | null {
    if (val === null || val === undefined || val === '') {
      return null;
    }
    const n = this.parseNumber(val);
    if (isNaN(n)) {
      return null;
    }
    return new Intl.NumberFormat(undefined, {useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2}).format(n);
  }

  private parseNumber(val: any): number {
    if (val === null || val === undefined || val === '') {
      return NaN;
    }
    const s = String(val).replace(/,/g, '');
    const n = Number(s);
    return n;
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Salesperson@@salesperson:Salesperson`);
    this.loadSysUsers();
    this.loadEntities();
    this.loadItemLookups();
    if (this.salespersonID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private loadItemLookups(): void {
    const params = '?state=active&pageNumber=0&pageSize=999999';
    this.table.getApiListData('item_type', params).subscribe({
      next: (r: any) => {
        this.itemTypes = r?.Data ?? r?.data ?? [];
      },
      error: (e) => console.error('Error loading item types:', e)
    });
    this.table.getApiListData('item_category', params).subscribe({
      next: (r: any) => {
        this.itemCategories = r?.Data ?? r?.data ?? [];
      },
      error: (e) => console.error('Error loading item categories:', e)
    });
    this.table.getApiListData('item_group', params).subscribe({
      next: (r: any) => {
        this.itemGroups = r?.Data ?? r?.data ?? [];
      },
      error: (e) => console.error('Error loading item groups:', e)
    });
    this.table.getApiListData('item', params).subscribe({
      next: (r: any) => {
        this.items = r?.Data ?? r?.data ?? [];
      },
      error: (e) => console.error('Error loading items:', e)
    });
  }

  private loadEntities(): void {
    this.table.getApiListData('entity', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.entities = result?.Data ?? result?.data ?? [];
      },
      error: (err) => console.error('Error loading entities:', err)
    });
  }

  private loadSysUsers(): void {
    this.table.getApiListData('sys_user', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.sysUsers = result?.Data ?? result?.data ?? [];
      },
      error: (err) => console.error('Error loading sys users:', err)
    });
  }

  private LoadData() {
    this.table.getData('salesperson', this.salespersonID).subscribe(
      (result: any) => {
        const data = (result && result.Data !== undefined) ? result.Data : result;
        this.salesperson = data as SalesPersonModel;
        this.salespersonForm.patchValue(this.salesperson);
        // Ensure rate displays with grouping and 2 decimals on load
        this.formatRate();
        this.formatBillRate();
        this.formatCostRate();
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      },
      (error: HttpErrorResponse) => {
        console.log(error + ' - ' + $localize`:Error loading Salesperson @@errorLoadingSalesperson:Error loading Salesperson`);
      }
    );
  }

  saveClose() {
    if (this.onSave(true)) {
    }
  }

  saveNew() {
    // Defer reset until async save completes inside onSave(false)
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    const fallback = '/admin/salesperson-list';
    this.nav.backOr(fallback);
  }

  private resetToDefaults(): void {
    this.salespersonForm.reset({
      name: '',
      rate: '',
      active: true,
      contractor: false,
      default_bill_rate: '',
      default_cost_rate: '',
      type_id: '',
      category_id: '',
      group_id: '',
      item_id: '',
      user_id: '',
      vendor_id: '',
    });
    this.salesperson = {id: null, name: '', rate: null, active: true, contractor: false} as unknown as SalesPersonModel;
    this.salespersonID = null;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete salesperson? @@areYouSureYouWantToDeleteSalesperson:Are you sure you want to delete salesperson?`,
      this.salesperson?.name,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('salesperson', this.salespersonID).subscribe(
          (result) => {
            retMsg = result;
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          },
          () => {
            this.onClose();
          }
        );
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      }
    );
  }

  onSave(saveClose: boolean) {
    this.submitted = true;
    if (this.salespersonForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return;
    }

    // Coerce number fields (strip separators and ensure number)
    const raw = {...this.salespersonForm.value};
    if (raw.rate !== null && raw.rate !== undefined && raw.rate !== '') {
      const parsed = this.parseNumber(raw.rate);
      raw.rate = isNaN(parsed) ? null : parsed;
    }
    if (raw.default_bill_rate !== null && raw.default_bill_rate !== undefined && raw.default_bill_rate !== '') {
      const parsed = this.parseNumber(raw.default_bill_rate);
      raw.default_bill_rate = isNaN(parsed) ? null : parsed;
    } else {
      raw.default_bill_rate = null;
    }
    if (raw.default_cost_rate !== null && raw.default_cost_rate !== undefined && raw.default_cost_rate !== '') {
      const parsed = this.parseNumber(raw.default_cost_rate);
      raw.default_cost_rate = isNaN(parsed) ? null : parsed;
    } else {
      raw.default_cost_rate = null;
    }

    // Only return the relevant ID field based on contractor flag
    if (raw.contractor) {
      raw.user_id = null;
    } else {
      raw.vendor_id = null;
    }

    this.salesperson = Object.assign({}, raw);

    if (this.salespersonID) {
      if (this.salespersonForm.invalid === false) {
        this.table.patch('salesperson', this.salespersonID, this.salesperson).then(
          () => {
            try {
              this.originalSnapshot = this.buildSnapshot();
            } catch {
            }
            if (saveClose) {
              this.onClose();
            }
          },
          (error: HttpErrorResponse) => {
            console.log(error);
          }
        ).finally(() => {
          this.submitted = false;
        });
      }
    } else {
      this.table.postWithPromise('salesperson', this.salesperson).then((res) => {
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.salespersonID = apiReturn?.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else {
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }
    return true;
  }

  buildSnapshot(): any {
    try {
      return this.salespersonForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
